import { VersionConfig } from '@/types/assessment';

export const collegeConfig: VersionConfig = {
  edition: 'college',
  label: 'College Edition',
  tagline: 'Your major doesn\'t have to be your destiny.',
  pathNames: { conservative: 'Degree to Career', moderate: 'Degree + Side Skills', bold: 'Build While You Learn' },
  timelines: ['This Semester', 'Next Semester', 'Over the Summer', 'Before Graduation', 'After Graduation'],
  socialPlatforms: ['Instagram', 'TikTok', 'LinkedIn'],
  screens: [
    {
      id: 'skills',
      title: 'How does your brain work?',
      subtitle: 'Pick up to 4 skills that feel most like you.',
      encouragement: 'These aren\'t resume bullets — they\'re how you actually think and operate.',
      type: 'chips-expandable',
      maxSelections: 4,
      options: [
        { id: 'analytical', label: 'Breaking Down Problems', description: 'You dissect things to understand them', looksLike: 'You ace case studies, love spreadsheets, or debug code for fun' },
        { id: 'creative', label: 'Thinking Outside the Box', description: 'You imagine things that don\'t exist yet', looksLike: 'You redesign class projects, create content, or prototype new ideas' },
        { id: 'communication', label: 'Making Complex Things Clear', description: 'You explain things so anyone can get it', looksLike: 'You nail presentations, write persuasive essays, or break down tough concepts' },
        { id: 'leadership', label: 'Stepping Up When Nobody Else Does', description: 'You take charge naturally', looksLike: 'You lead club meetings, organize events, or take point on group projects' },
        { id: 'tech-savvy', label: 'Picking Up Tech Fast', description: 'You figure out new tools before anyone else', looksLike: 'You automate your workflow, teach yourself new platforms, or build apps for fun' },
        { id: 'empathy', label: 'Reading the Room', description: 'You understand what people feel and need', looksLike: 'You mentor peers, mediate conflicts, or create spaces where everyone feels heard' },
        { id: 'adaptability', label: 'Rolling with the Punches', description: 'You handle change without panicking', looksLike: 'Switch majors? New city? Changed plans? You adjust and keep moving' },
        { id: 'research', label: 'Going Deep on Topics', description: 'You dig until you find the real answer', looksLike: 'You love literature reviews, long research sessions, or going deep on a thesis' },
        { id: 'design', label: 'Thinking About How Things Should Work', description: 'You care about design and user experience', looksLike: 'You prototype solutions, run user research, or notice when things are poorly designed' },
        { id: 'collaboration', label: 'Making Teams Click', description: 'You work better with others around you', looksLike: 'Hackathons, group research, startup teams — you thrive together' },
        { id: 'organizing', label: 'Keeping Everything in Order', description: 'You plan ahead and keep things on track', looksLike: 'Color-coded calendar, project timelines, you\'re the one who keeps the group on schedule' },
        { id: 'persuasion', label: 'Convincing People', description: 'You get buy-in for your ideas', looksLike: 'You win debates, pitch effectively, or get professors to bump your grade' },
      ],
    },
    {
      id: 'strengths',
      title: 'What sets you apart?',
      subtitle: 'Choose up to 5 strengths you\'re proud of.',
      encouragement: 'These are the things that make you stand out from every other student on campus.',
      type: 'chips-expandable',
      maxSelections: 5,
      options: [
        { id: 'pattern-recognition', label: 'Connecting the Dots', description: 'You see links others miss', looksLike: 'You connect coursework to real-world trends before professors do' },
        { id: 'storytelling', label: 'Telling a Great Story', description: 'You make ideas stick through narrative', looksLike: 'Your cover letters land, your pitches win, your presentations are memorable' },
        { id: 'resilience', label: 'Bouncing Back', description: 'You don\'t stay down when things go wrong', looksLike: 'Rejected from internships? You apply to 10 more. Bad grade? You own it and improve' },
        { id: 'curiosity', label: 'Always Wanting to Learn More', description: 'You can\'t stop exploring', looksLike: 'You take electives outside your major, attend random talks, and read constantly' },
        { id: 'hustle', label: 'Getting It Done', description: 'You make things happen, fast', looksLike: 'Side gig + full course load + club leadership: somehow it all works' },
        { id: 'vision', label: 'Seeing the Big Picture', description: 'You think ahead and plan for the long game', looksLike: 'You have a career plan and actually update it' },
        { id: 'calm-under-pressure', label: 'Staying Cool When It\'s Chaos', description: 'You\'re the steady one', looksLike: 'Finals week? Three deadlines? You organize, prioritize, and deliver' },
        { id: 'connector', label: 'Bringing People Together', description: 'You naturally build bridges', looksLike: 'You co-found clubs, introduce the right people, build cross-major friendships' },
        { id: 'self-starter', label: 'Starting Without Permission', description: 'You don\'t wait to be told what to do', looksLike: 'You launch projects solo, teach yourself skills, or cold-email people you admire' },
        { id: 'detail-oriented', label: 'Catching the Small Stuff', description: 'You notice what others walk past', looksLike: 'Typos jump out at you, your code compiles first try, your work is polished' },
      ],
    },
    {
      id: 'interests',
      title: 'What lights you up?',
      subtitle: 'Pick as many as you want. No limits here.',
      encouragement: 'Follow the energy. What would you do even if it wasn\'t for a grade?',
      type: 'chips',
      options: [
        { id: 'ai-tech', label: 'AI & Technology' },
        { id: 'arts', label: 'Art & Design' },
        { id: 'business', label: 'Business & Entrepreneurship' },
        { id: 'science', label: 'Science & Research' },
        { id: 'social-impact', label: 'Social Impact & Nonprofits' },
        { id: 'health', label: 'Health & Wellness' },
        { id: 'media', label: 'Media & Content Creation' },
        { id: 'education', label: 'Education & Teaching' },
        { id: 'law', label: 'Law & Policy' },
        { id: 'finance', label: 'Finance & Investing' },
        { id: 'environment', label: 'Environment & Sustainability' },
        { id: 'data', label: 'Data & Analytics' },
        { id: 'psychology', label: 'Psychology & Human Behavior' },
        { id: 'writing', label: 'Writing & Journalism' },
        { id: 'music', label: 'Music & Performing Arts' },
        { id: 'sports', label: 'Sports & Athletics' },
        { id: 'gaming', label: 'Gaming & Interactive Media' },
        { id: 'engineering', label: 'Engineering & Building Things' },
      ],
    },
    {
      id: 'inspiration',
      title: 'What inspires you right now?',
      subtitle: 'This helps us understand what you\'re drawn to outside of class.',
      encouragement: 'There are no right answers — we just want to know what catches your attention.',
      type: 'context',
      options: [],
      allowOpenText: true,
      openTextPlaceholder: 'What YouTube channels, podcasts, or accounts do you follow?\nAny books or articles that changed how you think?\nNewsletters or communities you\'re part of?',
    },
    {
      id: 'aspirations',
      title: 'Where do you see yourself heading?',
      subtitle: 'Pick up to 3 trending fields you\'re curious about.',
      encouragement: 'You don\'t have to commit. These are just the fields that catch your eye.',
      type: 'aspirations',
      maxSelections: 3,
      options: [
        { id: 'ai-ml', label: 'AI & Machine Learning' },
        { id: 'creator-economy', label: 'Creator Economy' },
        { id: 'climate-tech', label: 'Climate Tech' },
        { id: 'healthcare-ai', label: 'Healthcare + AI' },
        { id: 'fintech', label: 'Fintech & Digital Finance' },
        { id: 'consulting', label: 'Consulting & Strategy' },
        { id: 'product-management', label: 'Product Management' },
        { id: 'social-enterprise', label: 'Social Enterprise' },
        { id: 'cybersecurity', label: 'Cybersecurity' },
        { id: 'biotech', label: 'Biotech & Life Sciences' },
        { id: 'edtech', label: 'EdTech & Learning' },
        { id: 'gaming-vr', label: 'Gaming & XR/VR' },
        { id: 'space', label: 'Space & Aerospace' },
        { id: 'real-estate-tech', label: 'Real Estate & PropTech' },
      ],
    },
    {
      id: 'energy-workstyle',
      title: 'What\'s your energy type?',
      subtitle: 'How do you recharge, and how do you do your best work? Pick up to 5.',
      encouragement: 'Understanding your energy is the cheat code to not burning out in your 20s.',
      type: 'chips-expandable',
      maxSelections: 5,
      options: [
        { id: 'deep-focus', label: 'Deep Focus', description: 'Long, uninterrupted blocks of concentration', looksLike: 'Library all day, headphones on, phone in another room' },
        { id: 'burst-energy', label: 'Quick Bursts', description: 'Intense sprints then total decompression', looksLike: 'Crush a paper in 4 hours, then decompress with a walk and a snack' },
        { id: 'social-energy', label: 'People Energy', description: 'Energized by working alongside others', looksLike: 'Study groups, co-working sessions, brainstorms — the more people, the better' },
        { id: 'solo-creator', label: 'Solo Mode', description: 'You create your best work alone', looksLike: 'Solo studio time, independent research, your own rhythm and your own playlist' },
        { id: 'variety-seeker', label: 'Need to Switch It Up', description: 'You get bored doing the same thing too long', looksLike: 'Double major, three clubs, a startup idea, and a side project' },
        { id: 'steady-builder', label: 'Slow & Steady', description: 'Consistent, day-by-day progress', looksLike: 'Daily study schedule, habit tracker, small wins that compound' },
        { id: 'night-owl', label: 'Night Owl', description: 'Your best ideas come late at night', looksLike: 'Late-night coding sessions, essays written at 2am, creative breakthroughs after midnight' },
        { id: 'morning-person', label: 'Early Bird', description: 'You\'re sharpest before noon', looksLike: '7am gym then library, most productive in the morning, asleep by 11' },
      ],
    },
    {
      id: 'dream-wednesday',
      title: 'Picture your dream Wednesday',
      subtitle: 'Not a vacation — a regular work day, a couple years from now. What does it look like?',
      encouragement: 'This isn\'t about being realistic. It\'s about being honest.',
      type: 'dream-wednesday',
      vibeOptions: [
        { id: 'ceo-vibes', label: 'The Decision Maker', description: 'Meetings, decisions, leading a team toward something big', pros: ['High impact — your choices shape the direction', 'Leadership growth that compounds over time', 'People look to you for guidance'], cons: ['You\'re always "on" — hard to unplug', 'Heavy responsibility for outcomes', 'Less time for hands-on creative work'] },
        { id: 'creator-vibes', label: 'The Creator', description: 'Making things all day: writing, designing, building, coding', pros: ['Creative freedom to express your ideas', 'Flow states that make time disappear', 'A portfolio you\'re proud of'], cons: ['Income can be unpredictable early on', 'Requires serious self-discipline', 'Can feel isolating without a team'] },
        { id: 'explorer-vibes', label: 'The Explorer', description: 'New places, new people, new problems every day', pros: ['Never boring — constant novelty', 'Huge network of diverse contacts', 'Always learning something new'], cons: ['Less stability and routine', 'Harder to go deep on any one thing', 'Can be physically and mentally exhausting'] },
        { id: 'helper-vibes', label: 'The Helper', description: 'Teaching, mentoring, making someone\'s life genuinely better', pros: ['Deep sense of purpose and fulfillment', 'Strong community connections', 'Real, visible impact on actual people'], cons: ['Emotionally heavy — compassion fatigue is real', 'Some helping professions are underpaid', 'Burnout risk if boundaries aren\'t solid'] },
        { id: 'builder-vibes', label: 'The Builder', description: 'Starting something from scratch and watching it grow', pros: ['You own what you build — nobody can take that', 'Tangible results you can point to', 'Constant learning because everything is new'], cons: ['It\'s lonely before it\'s exciting', 'Lots of failures before the wins come', 'Long hours with no guaranteed payoff'] },
        { id: 'analyst-vibes', label: 'The Analyst', description: 'Finding answers in data, solving complex puzzles', pros: ['Companies need you and will pay for it', 'Clear right and wrong answers', 'Intellectually stimulating every day'], cons: ['Lots of screen time', 'Can feel repetitive without variety', 'Less people interaction than other paths'] },
      ],
      allowOpenText: true,
      openTextPlaceholder: 'Describe your ideal Wednesday in a few sentences...',
    },
    {
      id: 'values',
      title: 'What would you fight for?',
      subtitle: 'Pick up to 5 values that are non-negotiable for you.',
      encouragement: 'These aren\'t just words. They\'re the compass that keeps you on your path.',
      type: 'chips',
      maxSelections: 5,
      options: [
        { id: 'freedom', label: 'Freedom & Autonomy' },
        { id: 'security', label: 'Financial Security' },
        { id: 'creativity', label: 'Creativity & Expression' },
        { id: 'impact', label: 'Making an Impact' },
        { id: 'community', label: 'Community & Belonging' },
        { id: 'growth', label: 'Personal Growth' },
        { id: 'justice', label: 'Justice & Fairness' },
        { id: 'innovation', label: 'Innovation & Progress' },
        { id: 'balance', label: 'Work-Life Balance' },
        { id: 'recognition', label: 'Recognition & Status' },
        { id: 'adventure', label: 'Adventure & New Experiences' },
        { id: 'family', label: 'Family & Relationships' },
        { id: 'health', label: 'Health & Wellbeing' },
      ],
    },
    {
      id: 'barriers',
      title: 'What\'s in your way?',
      subtitle: 'Be honest. Knowing your barriers helps us build paths around them.',
      encouragement: 'Naming the obstacle is the first step to going around it.',
      type: 'barriers',
      options: [
        { id: 'no-major', label: 'No idea what I want to do with my major' },
        { id: 'unpaid-internships', label: 'Can\'t afford unpaid internships' },
        { id: 'imposter-syndrome', label: 'Imposter syndrome — I feel like I don\'t belong' },
        { id: 'no-network', label: 'No connections in my field of interest' },
        { id: 'burnout', label: 'Burnout from overcommitting' },
        { id: 'family-expectations', label: 'Family expects a specific career path' },
        { id: 'debt-anxiety', label: 'Student debt is weighing on my decisions' },
        { id: 'no-experience', label: 'No real-world experience yet' },
        { id: 'overwhelmed', label: 'Too many options — decision paralysis' },
      ],
    },
    {
      id: 'cognitive-style',
      title: 'How do you make decisions?',
      subtitle: 'Quick scenarios — just go with your gut.',
      encouragement: 'There are no right or wrong answers. Just your natural instincts.',
      type: 'cognitive-style',
      cognitiveScenarios: [
        {
          id: 'cs-1',
          scenario: 'Your professor gives an open-ended final project. You can do anything related to the course.',
          optionA: { label: 'I\'d analyze a real-world case study with data and frameworks', trait: 'Analytical' },
          optionB: { label: 'I\'d create something original: a prototype, film, or interactive experience', trait: 'Creative' },
        },
        {
          id: 'cs-2',
          scenario: 'You have a free summer. No obligations.',
          optionA: { label: 'I\'d line up a structured internship or research position', trait: 'Structured' },
          optionB: { label: 'I\'d travel, freelance, or work on my own project', trait: 'Exploratory' },
        },
        {
          id: 'cs-3',
          scenario: 'A startup founder offers you equity instead of a salary for a summer role.',
          optionA: { label: 'Too risky. I need income certainty right now', trait: 'Risk-Cautious' },
          optionB: { label: 'I\'m in. The learning and upside are worth it', trait: 'Risk-Seeking' },
        },
        {
          id: 'cs-4',
          scenario: 'You disagree with your team\'s direction on a major group project.',
          optionA: { label: 'I\'d present my concerns with evidence and suggest a pivot', trait: 'Diplomatic' },
          optionB: { label: 'I\'d build my alternative version to show why it\'s better', trait: 'Action-Oriented' },
        },
      ],
    },
    {
      id: 'experience',
      title: 'Any experience so far?',
      subtitle: 'Totally optional — internships, jobs, volunteering, or meaningful projects.',
      encouragement: 'This helps us match paths to what you\'ve actually done, not just what you\'re interested in.',
      type: 'context',
      options: [],
      allowOpenText: true,
      openTextPlaceholder: 'e.g., Summer internship at a startup, TA for intro CS, volunteered at campus food pantry, freelance design work, ran a campus org...',
    },
    {
      id: 'resume-upload',
      title: 'Upload your resume',
      subtitle: 'We\'ll extract your skills & experience to personalize your path.',
      encouragement: 'Your resume data stays private and is only used to personalize your results.',
      type: 'resume-upload',
    },
    {
      id: 'personalize',
      title: 'Last thing — what does your future look like?',
      subtitle: 'These answers help us match you with paths that actually fit your life after college.',
      encouragement: 'No wrong answers. Just be honest about what you want.',
      type: 'personalize',
    },
  ],
};
